import { useInitialSurfaceReady } from "../../../logic/hooks/useInitialSurfaceReady";
import { startupForOwner } from "../../../logic/helpers/appStartup";
import { useBundleOffers } from "../../../logic/hooks/useBundleOffers";
import { StandaloneBundleOfferDialog } from "../../common/StandaloneBundleOfferDialog/StandaloneBundleOfferDialog";
import { SoundButton } from "../../common/SoundButton/SoundButton";
// src/components/pages/MainPage/MainPage.tsx

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useSlice } from "@bridge-applications/provider-kit";
import type { ApiCommerceChainJourney } from "@bridge-applications/pb-types";
import { SimpleDialog } from "../../common/SimpleDialog/SimpleDialog";
import { SimpleDialogHeader } from "../../common/SimpleDialog/comps/SimpleDialogHeader";
import SlotMachine from "./SlotMachine/SlotMachine";
import PayTableDialogContent from "./PayTableDialogContent";
import { ReferralDrawer } from "./ReferralDrawer";
import palmtreeLeft from "../../../assets/static/images/decos/palmtree/deco-palmtree-left.webp";
import palmtreeRight from "../../../assets/static/images/decos/palmtree/deco-palmtree-right.webp";
import sunCore from "../../../assets/static/images/decos/deco-sun-core.webp";
import payTableButton from "../../../assets/static/images/buttons/pay-table-button.png";
import energyCanBox from "../../../assets/static/images/buttons/energy-can-box.webp";
import friendStealButton from "../../../assets/static/images/buttons/friend-steal-thick-outline.webp";
import dailyRewardButton from "../../../assets/static/images/daily-reward/daily-reward-button.webp";
import navGnomeButton from "../../../assets/static/images/buttons/nav-gnome-btn.png";
import navFishingButton from "../../../assets/static/images/buttons/nav-fishing-btn.png";
import "./MainPage.scss";
import {
    haptics,
    type HapticImpactStyle,
} from "../../../logic/helpers/haptics";
import { useInbox } from "../../../logic/hooks/useInbox";
import { InboxDialog } from "./components/InboxDialog/InboxDialog";
import { FriendStealDialog } from "./components/FriendStealDialog/FriendStealDialog";
import { DailyRewardDialog } from "./components/DailyRewardDialog/DailyRewardDialog";
import { RewardCollectionDialog } from "../../common/RewardCollectionDialog/RewardCollectionDialog";
import { useDailyReward } from "../../../logic/hooks/useDailyReward";
import { useSpinTogether } from "../../../logic/hooks/useSpinTogether";
import { isSpinTogetherEnabledForTelegramUser } from "../../../state/config";
import {
    activeChainJourney,
    useCommerce,
} from "../../../logic/hooks/useCommerce";
import { ChainOfferDialog } from "./components/ChainOfferDialog/ChainOfferDialog";
import { MainPageLaunchersRuntime } from "./components/MainPageLaunchersRuntime/MainPageLaunchersRuntime";
import { KeepPlayingOfferDialog } from "./components/KeepPlayingOfferDialog/KeepPlayingOfferDialog";
import { useKeepPlayingOffer } from "../../../logic/hooks/useKeepPlayingOffer";
import { useReferralCampaign } from "../../../logic/hooks/useReferralCampaign";
import { useCareerAccess } from "../../../logic/hooks/useCareerAccess";
import { canAutoOpenChainOffer } from "../../../logic/helpers/chainOfferAutoOpen";
import { useCareerUpdateBadge } from "../../../logic/hooks/useCareerUpdateBadge";
import { hasAffordableCareerQuest } from "../../../logic/helpers/careerQuestNotification";
import { MainPageNavButton } from "./components/MainPageNavButton/MainPageNavButton";

/** Timed to `@keyframes main-page-friend-steal-award` rotation extrema (900ms). */
const FRIEND_STEAL_AWARD_WIGGLE_HAPTICS: readonly {
    atMs: number;
    style: HapticImpactStyle;
}[] = [
    { atMs: 180, style: "medium" }, // 20%
    { atMs: 324, style: "light" }, // 36%
    { atMs: 468, style: "medium" }, // 52%
    { atMs: 612, style: "light" }, // 68%
    { atMs: 756, style: "soft" }, // 84%
];

export default function MainPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const launchState: unknown = location.state;
    const actionToken = location.pathname === "/inbox" && launchState && typeof launchState === "object"
        && "messageActionToken" in launchState && typeof launchState.messageActionToken === "string"
        ? launchState.messageActionToken : undefined;
    const { state: authState } = useSlice("authSlice");
    const careerAccess = useCareerAccess();
    const careerUpdateBadge = useCareerUpdateBadge(authState.userId);
    const { state: careerState } = useSlice("careerSlice");
    const chainAutoOpenUnlocked =
        authState.bootstrapStatus === "ready" &&
        canAutoOpenChainOffer(
            authState.userId,
            careerState.snapshotUserId,
            careerState.snapshot,
        );
    const inbox = useInbox(authState.bootstrapStatus === "ready", actionToken);
    const dailyReward = useDailyReward(authState.bootstrapStatus === "ready");
    const spinTogetherEnabled = isSpinTogetherEnabledForTelegramUser(
        authState.telegramUserId,
    );
    const spinTogether = useSpinTogether(
        authState.bootstrapStatus === "ready" && spinTogetherEnabled,
    );
    const commerce = useCommerce(authState.bootstrapStatus === "ready");
    const { state: slotState } = useSlice("slotMachineSlice");
    const mainRef = useRef<HTMLDivElement>(null);
    const { setSlice: setStartup } = useSlice("appStartupSlice");
    useInitialSurfaceReady(
        mainRef,
        slotState.loadStatus === "ready" && slotState.snapshot !== null,
    );

    const hasAffordableQuest = hasAffordableCareerQuest(
        slotState.snapshot,
        authState.userId,
        careerState.snapshotUserId === authState.userId
            ? careerState.snapshot
            : null,
    );
    const keepPlaying = useKeepPlayingOffer(commerce, slotState.snapshot);
    const [isPayTableOpen, setIsPayTableOpen] = useState(false);
    const [isInboxOpen, setIsInboxOpen] = useState(false);
    const [isFriendStealOpen, setIsFriendStealOpen] = useState(false);
    const [friendStealAwardRevision, setFriendStealAwardRevision] = useState(0);
    const [friendStealCount, setFriendStealCount] = useState<number>();
    const [isEnergyDrawerOpen, setIsEnergyDrawerOpen] = useState(false);
    const referralCampaign = useReferralCampaign({
        enabled: authState.bootstrapStatus === "ready",
        isOpen: isEnergyDrawerOpen,
        userId: authState.userId,
        telegramUserId: authState.telegramUserId,
    });
    const showEnergyLauncher =
        authState.bootstrapStatus === "no-telegram" ||
        authState.bootstrapStatus === "error" ||
        (authState.bootstrapStatus === "ready" &&
            referralCampaign.referral !== null &&
            referralCampaign.referral.state !== "REWARDED");
    const [isChainOfferOpen, setIsChainOfferOpen] = useState(false);
    const [retainedChainJourney, setRetainedChainJourney] =
        useState<ApiCommerceChainJourney | null>(null);
    const [startEnergyDrawerOnPurchase, setStartEnergyDrawerOnPurchase] =
        useState(false);
    const closePayTable = () => setIsPayTableOpen(false);
    const activeChainPresentation =
        commerce.activePresentation?.targetKind === "CHAIN_JOURNEY"
            ? commerce.activePresentation
            : null;
    const activeServerChainJourney = (() => {
        if (!activeChainPresentation) {
            return activeChainJourney(commerce.snapshot);
        }
        if (!commerce.snapshot) {
            return activeChainPresentation.chainJourney;
        }
        const latest = commerce.snapshot.chainJourneys.find(
            (journey) =>
                journey.journeyId ===
                activeChainPresentation.chainJourney.journeyId,
        );
        return latest?.status === "ACTIVE" ? latest : null;
    })();
    const chainJourney = activeServerChainJourney ?? retainedChainJourney;
    const bundles = useBundleOffers(
        authState.bootstrapStatus === "ready",
        dailyReward.loadStatus === "ready" && !dailyReward.isPresenting &&
        !commerce.isCollectingRewards &&
        commerce.loadStatus !== "loading" && commerce.loadStatus !== "idle" &&
        !(activeChainPresentation?.autoOpen && chainAutoOpenUnlocked) &&
        !isChainOfferOpen && !isInboxOpen && !isPayTableOpen && !isFriendStealOpen &&
        !isEnergyDrawerOpen && !keepPlaying.isOpen && location.pathname !== "/inbox" &&
        slotState.spinStatus === "idle",
        commerce,
    );
    const closeEnergyDrawer = () => setIsEnergyDrawerOpen(false);
    const closeChainOffer = () => {
        setIsChainOfferOpen(false);
        if (retainedChainJourney?.status === "COMPLETED") {
            setRetainedChainJourney(null);
        }
        if (activeChainPresentation) {
            commerce.dismissPresentation(
                activeChainPresentation.presentationId,
            );
        }
    };
    const openInbox = () => {
        haptics.impact("light");
        setIsInboxOpen(true);
        void inbox.refresh();
    };
    const closeInbox = () => {
        setIsInboxOpen(false);
        if (location.pathname === "/inbox") {
            navigate("/", { replace: true });
        }
    };
    const openFriendStealFromInbox = () => {
        haptics.impact("light");
        closeInbox();
        setIsFriendStealOpen(true);
    };
    const [renderGnomeFishingButtons] = useState(false);
    const awardHapticTimersRef = useRef<number[]>([]);

    const clearAwardHaptics = useCallback(() => {
        for (const timerId of awardHapticTimersRef.current) {
            window.clearTimeout(timerId);
        }
        awardHapticTimersRef.current = [];
    }, []);

    useEffect(() => () => clearAwardHaptics(), [clearAwardHaptics]);

    useEffect(() => {
        if (activeServerChainJourney === null) return;
        setRetainedChainJourney((current) =>
            current?.journeyId === activeServerChainJourney.journeyId &&
            current.version === activeServerChainJourney.version
                ? current
                : activeServerChainJourney,
        );
    }, [activeServerChainJourney]);

    useEffect(() => {
        if (retainedChainJourney === null || commerce.snapshot === null) {
            return;
        }

        const latestJourney = commerce.snapshot.chainJourneys.find(
            (candidate) =>
                candidate.journeyId === retainedChainJourney.journeyId,
        );
        if (
            latestJourney === undefined ||
            (latestJourney.status !== "ACTIVE" &&
                latestJourney.status !== "COMPLETED")
        ) {
            if (isChainOfferOpen) setIsChainOfferOpen(false);
            setRetainedChainJourney(null);
            return;
        }

        if (latestJourney.version !== retainedChainJourney.version) {
            setRetainedChainJourney(latestJourney);
        }
    }, [commerce.snapshot, isChainOfferOpen, retainedChainJourney]);

    useEffect(() => {
        // Voluntary launcher access and claims remain available before job 1.
        if (commerce.isCollectingRewards || bundles.selected || !activeChainPresentation?.autoOpen || !chainAutoOpenUnlocked)
            return;

        // Daily check-in owns the first automatic dialog when it has a reward.
        // Queue the chain popup until collection has also finished animating.
        if (dailyReward.loadStatus === "ready" && !dailyReward.isPresenting) {
            setIsChainOfferOpen(true);
        }
    }, [
        commerce.isCollectingRewards,
        bundles.selected,
        activeChainPresentation,
        chainAutoOpenUnlocked,
        dailyReward.isPresenting,
        dailyReward.loadStatus,
    ]);

    useEffect(() => {
        if (!isChainOfferOpen || !activeChainPresentation) return;
        void commerce.recordPresentation(activeChainPresentation);
    }, [
        activeChainPresentation,
        commerce.recordPresentation,
        isChainOfferOpen,
    ]);

    useEffect(() => {
        if (activeServerChainJourney !== null) return;

        const latestRetainedJourney = commerce.snapshot?.chainJourneys.find(
            (candidate) =>
                candidate.journeyId === retainedChainJourney?.journeyId,
        );
        if (isChainOfferOpen && latestRetainedJourney?.status === "COMPLETED") {
            return;
        }

        if (chainJourney === null || chainJourney.status !== "COMPLETED") {
            setIsChainOfferOpen(false);
        }
    }, [
        activeServerChainJourney,
        chainJourney,
        commerce.snapshot,
        isChainOfferOpen,
        retainedChainJourney?.journeyId,
    ]);

    const presentFriendStealInventory = useCallback(
        (availableGrantCount: number, awarded: boolean) => {
            setFriendStealCount(availableGrantCount);
            if (!awarded) return;

            setFriendStealAwardRevision((current) => current + 1);
            clearAwardHaptics();
            for (const beat of FRIEND_STEAL_AWARD_WIGGLE_HAPTICS) {
                awardHapticTimersRef.current.push(
                    window.setTimeout(() => {
                        haptics.impact(beat.style);
                    }, beat.atMs),
                );
            }
        },
        [clearAwardHaptics],
    );

    return (
        <div ref={mainRef} className="main-page">
            {/* ___sun disc in the upper area; sits behind everything else */}
            <img
                className="main-page__sun"
                src={sunCore}
                alt=""
                aria-hidden="true"
                draggable={false}
            />

            {/* ___decorative palm fronds draping in from the top corners; sit
                behind the slot machine and never intercept taps */}
            <img
                className="main-page__palm main-page__palm--left"
                src={palmtreeLeft}
                alt=""
                aria-hidden="true"
                draggable={false}
            />
            <img
                className="main-page__palm main-page__palm--right"
                src={palmtreeRight}
                alt=""
                aria-hidden="true"
                draggable={false}
            />

            {/* ___pay-table button (top-right); opens the paytable later */}
            <SoundButton
                type="button"
                className="main-page__paytable"
                aria-label={t("mainPage.payTable.ariaLabel")}
                onClick={() => {
                    haptics.impact("light");
                    setIsPayTableOpen(true);
                }}
            >
                <img
                    className="main-page__paytable__icon"
                    src={payTableButton}
                    alt=""
                    draggable={false}
                />
                <span className="main-page__paytable__label">
                    {t("mainPage.payTable.label")}
                </span>
            </SoundButton>

            <SoundButton
                type="button"
                className="main-page__daily-reward"
                aria-label={t("dailyReward.launcherAriaLabel")}
                onClick={() => {
                    haptics.impact("light");
                    dailyReward.open();
                }}
            >
                <img src={dailyRewardButton} alt="" draggable={false} />
            </SoundButton>

            {import.meta.env.DEV && (
                <SoundButton
                    type="button"
                    className="main-page__haptic-lab"
                    onClick={() => navigate("/dev/haptics")}
                >
                    {t("hapticLab.title")}
                </SoundButton>
            )}

            <MainPageLaunchersRuntime
                bundleOffers={bundles.offers}
                onOpenBundle={bundles.open}
                notificationCount={inbox.notificationCount}
                chainJourney={
                    isChainOfferOpen ? null : activeServerChainJourney
                }
                onOpenInbox={openInbox}
                onOpenChain={() => setIsChainOfferOpen(true)}
                onOpenWheel={(wheelCode) => {
                    haptics.impact("light");
                    navigate(`/wheels/${encodeURIComponent(wheelCode)}`);
                }}
            />

            {showEnergyLauncher && (
                <SoundButton
                    type="button"
                    className="main-page__energy-can"
                    aria-label={t("mainPage.energy.ariaLabel")}
                    onClick={() => {
                        haptics.impact("light");
                        setStartEnergyDrawerOnPurchase(false);
                        setIsEnergyDrawerOpen(true);
                    }}
                >
                    <img src={energyCanBox} alt="" draggable={false} />
                    <span className="main-page__energy-can__label">
                        {t("mainPage.energy.label")}
                    </span>
                </SoundButton>
            )}

            <SoundButton
                type="button"
                className="main-page__friend-steals"
                aria-label={t("friendSteal.launcher.ariaLabel", {
                    count: friendStealCount ?? 0,
                })}
                onClick={() => {
                    haptics.impact("light");
                    setIsFriendStealOpen(true);
                }}
            >
                <span
                    key={`friend-steal-award-${friendStealAwardRevision}`}
                    className={[
                        "main-page__friend-steals-content",
                        friendStealAwardRevision > 0 && "is-rewarded",
                    ]
                        .filter(Boolean)
                        .join(" ")}
                >
                    <span className="main-page__friend-steals-artwork">
                        <svg
                            width="0"
                            height="0"
                            aria-hidden="true"
                            focusable="false"
                        >
                            <defs>
                                <filter
                                    id="friend-steals-count-outline"
                                    x="-50%"
                                    y="-50%"
                                    width="200%"
                                    height="200%"
                                    colorInterpolationFilters="sRGB"
                                >
                                    <feMorphology
                                        in="SourceAlpha"
                                        operator="dilate"
                                        radius="1.5"
                                        result="expanded"
                                    />
                                    <feFlood floodColor="#fff" result="white" />
                                    <feComposite
                                        in="white"
                                        in2="expanded"
                                        operator="in"
                                        result="outline"
                                    />
                                    <feMerge>
                                        <feMergeNode in="outline" />
                                        <feMergeNode in="SourceGraphic" />
                                    </feMerge>
                                </filter>
                            </defs>
                        </svg>
                        <img
                            className="main-page__friend-steals-icon"
                            src={friendStealButton}
                            alt=""
                            draggable={false}
                        />
                        {friendStealCount !== undefined && (
                            <span className="main-page__friend-steals-count">
                                {friendStealCount}
                            </span>
                        )}
                    </span>
                    <span className="main-page__friend-steals-label">
                        {t("friendSteal.launcher.label")}
                    </span>
                </span>
            </SoundButton>

            {careerAccess === "allowed" && (
                <MainPageNavButton
                    className="main-page__nav main-page__nav--left"
                    label={t("mainPage.navigation.gnomeAriaLabel")}
                    icon={navGnomeButton}
                    isUpdated={careerUpdateBadge.isUpdated}
                    notificationLabel={
                        hasAffordableQuest
                            ? t(
                                  "mainPage.navigation.affordableQuestNotification",
                              )
                            : undefined
                    }
                    onClick={() => {
                        careerUpdateBadge.markClicked();
                        haptics.impact("light");
                        if (authState.userId) {
                            const owner = {
                                userId: authState.userId,
                                generation: authState.sessionGeneration,
                            };
                            setStartup((previous) => ({
                                ...startupForOwner(previous, owner),
                                gnomeNavigationAt: performance.now(),
                            }));
                        }
                        navigate("/gnome/career");
                    }}
                />
            )}

            {/* ___bottom-corner navigation buttons anchored to the usable area */}
            {renderGnomeFishingButtons && (
                <>
                    <MainPageNavButton
                        className="main-page__nav main-page__nav--right"
                        label={t("mainPage.navigation.fishingAriaLabel")}
                        icon={navFishingButton}
                    />
                </>
            )}

            <SlotMachine
                onRequestEnergy={() => {
                    setStartEnergyDrawerOnPurchase(true);
                    setIsEnergyDrawerOpen(true);
                }}
                onFriendStealPresentation={presentFriendStealInventory}
                spinTogether={spinTogether}
            />

            <ReferralDrawer
                campaign={referralCampaign}
                isOpen={isEnergyDrawerOpen}
                onClose={closeEnergyDrawer}
                startOnPurchase={startEnergyDrawerOnPurchase}
            />

            {keepPlaying.offer && (
                <KeepPlayingOfferDialog
                    isOpen={keepPlaying.isOpen}
                    offer={keepPlaying.offer}
                    refill={keepPlaying.refill}
                    availability={keepPlaying.availability}
                    isBusy={keepPlaying.isBusy}
                    error={keepPlaying.error}
                    onClose={keepPlaying.close}
                    onPurchase={keepPlaying.purchase}
                />
            )}

            {bundles.selected && (
                <StandaloneBundleOfferDialog
                    offer={bundles.selected}
                    clockOffsetMs={bundles.clockOffsetMs}
                    availability={bundles.availability}
                    isBusy={bundles.isBusy}
                    error={bundles.error}
                    onClose={bundles.close}
                    onPurchase={() => void bundles.purchase()}
                />
            )}
            <ChainOfferDialog
                isOpen={isChainOfferOpen}
                journey={chainJourney}
                isBusy={
                    commerce.mutationStatus !== "idle" ||
                    commerce.checkoutStatus === "pending" ||
                    commerce.snapshot === null
                }
                error={commerce.error}
                onClose={closeChainOffer}
                onClaim={commerce.claimChainStep}
                onPurchase={commerce.purchaseOffer}
            />

            <FriendStealDialog
                isOpen={isFriendStealOpen}
                onClose={() => setIsFriendStealOpen(false)}
            />

            <DailyRewardDialog
                isOpen={dailyReward.isDialogOpen}
                snapshot={dailyReward.snapshot}
                grants={dailyReward.grants}
                loadStatus={dailyReward.loadStatus}
                loadError={dailyReward.loadError}
                claimState={dailyReward.claimState}
                onClose={dailyReward.close}
                onClaim={dailyReward.claim}
                onRetry={dailyReward.retry}
            />
            <RewardCollectionDialog
                presentation={dailyReward.collectionPresentation}
                onCollect={dailyReward.fadeCollectionSound}
                onComplete={dailyReward.completeCollection}
            />

            <SimpleDialog
                className="paytable-dialog"
                isOpen={isPayTableOpen}
                onClose={closePayTable}
                aria-labelledby="paytable-title"
                header={
                    <SimpleDialogHeader
                        title={t("mainPage.payTableDialog.title")}
                        titleId="paytable-title"
                        closeLabel={t("mainPage.payTableDialog.closeLabel")}
                        onClose={closePayTable}
                    />
                }
            >
                <PayTableDialogContent />
            </SimpleDialog>

            <InboxDialog
                inbox={inbox}
                isOpen={isInboxOpen || location.pathname === "/inbox"}
                onClose={closeInbox}
                onRequestFriendSteal={openFriendStealFromInbox}
                spinTogether={spinTogether}
            />
        </div>
    );
}
